/**
 * voice-v8 — Vorlese-Hilfe an der Antwort-Blase: „Weiter ab …“ und Vorlese-Karte mit Schieber (19.10.2026).
 *
 * Martin: Lange Texte nicht als endlose Wand, sondern als Player mit Schieber — und nach einer Unterbrechung dort
 * weiterlesen, nicht von vorn.
 *
 * ⚠️ KEINE zweite Wiedergabe. Die Karte spielt selbst nichts: Ihr Knopf löst den Vorlese-Knopf der Blase aus
 *    (Laden/Spielen/Pause/Weiter, Pille stoppt — alles wie bisher), der Schieber und „Von vorn“ schicken ihm das Ereignis
 *    `voice-v8:vorlese-ab` (app.ts → playTts mit Versatz). Die Stelle meldet die TtsQueue je fertigem Stück als
 *    `voice-v8:vorlese-stand` am Knopf (vorlese-fortsetzen.ts).
 *
 * ⚠️ Grenze (ehrlich): Das Vorlesen im Chat ist eine Streaming-Vertonung je Stück — es gibt keine Gesamtdauer und keine
 *    fertige Datei, darum auch keine Zeit-Anzeige und kein Ziehen auf Sekunden (buildAudioPlayer, audio-player.ts, ist ein
 *    <audio>-Spieler und passt dafür nicht). Der Schieber zählt ABSCHNITTE des Textes: „Teil 3 von 12“. Er rückt nach
 *    jedem fertig gesprochenen Stück vor (nicht stufenlos), Loslassen springt an den Anfang des Abschnitts.
 *
 * 21.10.2026 (Martin 19:01: „Was ist das für ein Player und warum sieht das so komisch aus?“): Die Karte war ein großer,
 *    fast leerer Kasten — Wort-Knopf links, Zähler rechts, darunter ein Schieber, von dem im Safari nur der Punkt zu
 *    sehen war. Jetzt EINE kompakte Zeile: runder Knopf (44) mit Zeichen, darüber was gerade passiert („Liest vor“) und
 *    „Teil 2 von 7“, darunter die Teile des Textes als Balken (gehört / jetzt / offen), rechts „Stopp“, solange etwas
 *    läuft. Der Schieber bleibt derselbe (`input[type=range]`, dieselben Ereignisse), er liegt unsichtbar über den
 *    Teilen — Tippen/Ziehen/Pfeiltasten wie bisher. „Stopp“ ist der Stopp des Mini-Players (`quelle.stoppe()`), kein
 *    neuer Weg; Restzeit gibt es nicht, weil das Streaming keine Gesamtdauer kennt (siehe oben).
 */
import { abschnittsStarts, hinweisAb, sollKarte, FORTSETZEN_MIN_ZEICHEN } from "./vorlese-fortsetzen.js";
import { holeQuelle, teilText } from "./vorlese-quelle.js";

export { EREIGNIS_STAND, EREIGNIS_AB, SPRUNG_PAUSE_MS } from "./vorlese-quelle.js";

/** Name des runden Knopfs (aria-label) je Zustand — wie bisher das Wort auf dem Knopf. */
const KNOPF_WORT: Record<string, string> = {
  idle: "Vorlesen",
  loading: "Lädt …",
  playing: "Pause",
  paused: "Weiter",
  wartet: "Weiterspielen",
};

/** Ab so vielen Teilen zeigt die Leiste einen durchgehenden Streifen statt einzelner Teile mit Abstand. */
export const VIELE_TEILE = 20;

/** Was gerade passiert — sichtbar neben dem Knopf. */
const STATUS_WORT: Record<string, string> = {
  idle: "Vorlesen",
  loading: "Lädt …",
  playing: "Liest vor",
  paused: "Pausiert",
  wartet: "Bitte tippen",
};

const ZEICHEN_PLAY = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
const ZEICHEN_PAUSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
const ZEICHEN_LAEDT = '<svg class="vorlese-karte-spinner" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor"><path d="M12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6V4z"/></svg>';
const ZEICHEN_STOPP = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>';

/** Zeichen im runden Knopf: Pause, solange es spricht; Spinner beim Laden; sonst Play. */
function zeichenFuer(zustand: string): string {
  if (zustand === "playing") return ZEICHEN_PAUSE;
  if (zustand === "loading") return ZEICHEN_LAEDT;
  return ZEICHEN_PLAY;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/**
 * @param knopf der Vorlese-Knopf der Blase (`.bubble-play`), dessen Wiedergabe die Karte steuert
 * @param sprech der Sprechtext (Maßstab der Zeichen-Versätze, siehe app.ts `sprechTextVon`)
 * @returns die Hilfe zum Anhängen unter den Knopf, oder null bei kurzen Texten
 */
export function baueVorleseHilfe(knopf: HTMLButtonElement, sprech: string): HTMLElement | null {
  if (sprech.length < FORTSETZEN_MIN_ZEICHEN) return null;
  const quelle = holeQuelle(knopf, sprech);
  const wrap = el("div", "vorlese-hilfe");

  // --- „Weiter ab …“ + „Von vorn“ ---
  const hinweis = el("div", "vorlese-hinweis");
  const hinweisText = el("span", "vorlese-hinweis-text");
  hinweisText.setAttribute("role", "status");
  const vonVorn = el("button", "vorlese-vonvorn", "Von vorn");
  vonVorn.type = "button";
  hinweis.append(hinweisText, vonVorn);
  hinweis.hidden = true;
  wrap.append(hinweis);

  const starts = abschnittsStarts(sprech);

  // --- Vorlese-Karte (nur lange Texte): eine Zeile — Knopf · Status + Teile · Stopp ---
  let seek: HTMLInputElement | null = null;
  let karteKnopf: HTMLButtonElement | null = null;
  let abschnittText: HTMLElement | null = null;
  let statusText: HTMLElement | null = null;
  let stopp: HTMLButtonElement | null = null;
  let teile: HTMLElement[] = [];
  if (sollKarte(sprech.length) && starts.length > 1) {
    const karte = el("div", "vorlese-karte");
    karte.setAttribute("role", "group");
    karte.setAttribute("aria-label", "Vorlesen");
    karteKnopf = el("button", "vorlese-karte-knopf");
    karteKnopf.type = "button";

    const mitte = el("div", "vorlese-karte-mitte");
    const kopf = el("div", "vorlese-karte-kopf");
    statusText = el("span", "vorlese-karte-status");
    abschnittText = el("span", "vorlese-karte-abschnitt");
    kopf.append(statusText, abschnittText);

    // Die Teile sind nur Bild (aria-hidden) — die Stelle sagt der Schieber darüber (aria-valuetext) und der Zähler.
    const leiste = el("div", "vorlese-karte-leiste");
    const balken = el("div", "vorlese-karte-teile");
    balken.setAttribute("aria-hidden", "true");
    // Viele Teile (Prüfer 21.10.): ab VIELE_TEILE ein durchgehender Streifen ohne Lücken — Einzelteile mit Abstand liefen am
    // schmalen Telefon über die Karte hinaus.
    balken.classList.toggle("is-durchgehend", starts.length > VIELE_TEILE);
    teile = starts.map(() => el("span", "vorlese-karte-teil"));
    balken.append(...teile);
    seek = el("input", "vorlese-karte-schieber");
    seek.type = "range";
    seek.min = "0";
    seek.max = String(starts.length - 1);
    seek.step = "1";
    seek.setAttribute("aria-label", "Vorlese-Position (Teil)");
    leiste.append(balken, seek);
    mitte.append(kopf, leiste);

    stopp = el("button", "vorlese-karte-stopp");
    stopp.type = "button";
    stopp.setAttribute("aria-label", "Vorlesen beenden");
    stopp.title = "Stopp";
    stopp.innerHTML = ZEICHEN_STOPP;
    stopp.hidden = true;

    karte.append(karteKnopf, mitte, stopp);
    wrap.append(karte);
    karteKnopf.addEventListener("click", () => quelle.umschalten());
    // Stopp blendet sich selbst aus: der Tastaturfokus darf dabei nicht auf <body> fallen — er geht zum Knopf daneben.
    const knopfFuerFokus = karteKnopf;
    stopp.addEventListener("click", () => {
      const hatteFokus = document.activeElement === stopp;
      quelle.stoppe();
      if (hatteFokus) knopfFuerFokus.focus();
    });
  }

  /** Zähler, Schieber-Ansage und Teile auf Teil `i` stellen. */
  const zeigeTeil = (i: number): void => {
    if (!seek || !abschnittText) return;
    const t = teilText(i, starts.length);
    abschnittText.textContent = t;
    seek.setAttribute("aria-valuetext", t);
    teile.forEach((teil, n) => {
      teil.classList.toggle("is-gehoert", n < i);
      teil.classList.toggle("is-jetzt", n === i);
    });
  };

  const zeichne = (): void => {
    const z = quelle.zustand();
    const versatz = quelle.versatz();
    hinweis.hidden = !(versatz > 0 && z === "idle");
    if (!hinweis.hidden) hinweisText.textContent = hinweisAb(sprech, versatz);
    if (karteKnopf && statusText) {
      const wort = KNOPF_WORT[z] ?? "Vorlesen";
      if (karteKnopf.dataset.state !== z) karteKnopf.innerHTML = zeichenFuer(z);
      karteKnopf.dataset.state = z;
      karteKnopf.setAttribute("aria-label", wort === "Vorlesen" ? "Antwort vorlesen" : wort);
      karteKnopf.title = wort;
      statusText.textContent = STATUS_WORT[z] ?? "Vorlesen";
    }
    if (stopp) stopp.hidden = !quelle.laeuft();
    if (seek && document.activeElement !== seek) {
      const i = quelle.abschnitt();
      seek.value = String(i);
      zeigeTeil(i);
    }
  };

  vonVorn.addEventListener("click", () => quelle.vonVorn());
  if (seek) {
    const s = seek;
    s.addEventListener("input", () => zeigeTeil(Number(s.value)));
    // Loslassen springt (gebündelt in der Quelle: mehrere Pfeiltasten-Schritte ergeben EINEN Strom).
    s.addEventListener("change", () => quelle.springeZuAbschnitt(Number(s.value)));
  }
  quelle.abonniere(zeichne);
  zeichne();
  return wrap;
}
